import { Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import { useState } from 'react'
import { useNotify, useRefresh } from 'react-admin'
import { Checkbox } from 'shared/components/Checkbox/Checkbox'
import { RadioButtonGroup } from 'shared/components/RadioButtonGroup/RadioButtonGroup'
import { Text } from 'shared/components/Text/Text'
import { Handler } from 'shared/helpers/typeHelper'
import { User } from 'shared/models/user'

import { updateTeamUserSubscriptionItems } from '../../api/core'
import { hasPermission, Permissions } from '../../Permissions'

interface UserProgramEnrollmentProps {
  record: User
  children: (handleProgramEnrollment?: Handler) => React.ReactNode
  onSuccess?: Handler
  permissions: any
}

export function UserProgramEnrollment({
  children,
  record,
  onSuccess,
  permissions,
}: UserProgramEnrollmentProps) {
  const [state, setState] = useState({
    dispatchServiceChecked: false,
    truckLeaseChecked: false,
    dispatchServiceOption: 'charge',
  })

  const { dispatchServiceChecked, truckLeaseChecked, dispatchServiceOption } = state
  const [open, setOpen] = useState(false)

  const notify = useNotify()
  const refresh = useRefresh()
  const canManageProgramEnrollment = hasPermission(permissions || [], Permissions.ProgramEnrollment)

  const handleChange = (name: string) => (checked: boolean) => {
    setState({ ...state, [name]: checked })
  }

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, dispatchServiceOption: event.target.value })
  }

  const setProgramEnrollmentCheckboxes = () => {
    setState({
      dispatchServiceChecked: record?.defaultTeamMembership?.hasDispatchServiceFeature ?? false,
      truckLeaseChecked: record?.defaultTeamMembership?.hasTruckLeaseFeature ?? false,
      dispatchServiceOption: 'charge',
    })
  }

  const handleUpdate = async (dispatchServiceChecked: boolean, truckLeaseChecked: boolean) => {
    const programEnrollmentFeatures = {
      program_enrollment_features: {
        DISPATCH_SERVICE: dispatchServiceChecked && dispatchServiceOption === 'charge',
        DISPATCH_SERVICE_FREE: dispatchServiceChecked && dispatchServiceOption === 'free',
        TRUCK_LEASE: truckLeaseChecked,
      },
    }
    updateTeamUserSubscriptionItems(record.id, programEnrollmentFeatures)
      .then(() => {
        setOpen(false)
        notify('Success!', { type: 'success' })
        refresh()
        onSuccess?.()
      })
      .catch((error: any) => {
        setOpen(false)
        setProgramEnrollmentCheckboxes()
        notify(JSON.stringify(error.response.data), { type: 'error' })
      })
  }

  const handleClick = () => {
    setProgramEnrollmentCheckboxes()
    setOpen(true)
  }

  return (
    <>
      {children(canManageProgramEnrollment ? handleClick : undefined)}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogContent>
          <Typography variant="h5"> Manage Program Enrollment </Typography>
          <Checkbox
            name="truckLeaseChecked"
            value={truckLeaseChecked}
            onChange={handleChange('truckLeaseChecked')}
            label={<Text variant="label2">Truck Lease</Text>}
          />
          <Checkbox
            name="dispatchServiceChecked"
            value={dispatchServiceChecked}
            onChange={handleChange('dispatchServiceChecked')}
            label={<Text variant="label2">Dispatch Service</Text>}
          />
          {dispatchServiceChecked && (
            <div style={{ marginLeft: '20px' }}>
              <RadioButtonGroup
                options={[
                  {
                    label: '3% charge',
                    value: 'charge',
                  },
                  {
                    label: 'Free',
                    value: 'free',
                  },
                ]}
                value={dispatchServiceOption}
                onChange={handleRadioChange}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleUpdate(dispatchServiceChecked, truckLeaseChecked)}
          >
            Confirm
          </Button>
          <Button variant="text" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
